import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { kebabCase, startCase } from "lodash";
import { useSearchParams } from "react-router-dom";

import { BoxedContent } from "@/components/common";
import {
  getDynamicObjectRecords,
  getDynamicObjectRecordById,
} from "@/api/dynamic/dynamicObjectNameApi";
import { DynamicFormContainer } from "@/components/dynamic";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { AlertModal } from "@/components/modals";
import { TableWithCheckbox } from "@/components/finance/account-receivables";
import useTaskRedirect from "@/hooks/useTaskRedirect";
import { getPurchaseOrderTableColumns } from "@/components/finance/account-receivables/tableWithCheckboxData";
import { useModal } from "@/hooks";

const formatPurchaseOrder = (purchaseOrder) => {
  const {
    id,
    number,
    itemDetail,
    discount,
    discountType,
    subtotal,
    total,
    itemType,
    supplier,
  } = purchaseOrder;

  const data = {
    key: id,
    supplier: {
      label: supplier.name,
      value: supplier.id,
    },
    purchaseOrder: {
      label: number,
      value: id,
    },
    itemDetail: itemDetail.filter((i) => i.received !== "Received").map((item) => {
      const { quantity, receivedQuantity } = item;

      const remainingQuantity =
        Number(quantity) - Number(receivedQuantity || 0);

      return {
        purchaseOrderItemDetailId: item.id,
        item: {
          label: item.item.itemName,
          value: item.item.id,
        },
        deliveryLocation: item.deliveryLocation ?
          {
            label: item.deliveryLocation.name,
            value: item.deliveryLocation.id,
          } :
          null,
        rate: item.rate,
        acceptedQuantity: remainingQuantity,
        suppliedQuantity: remainingQuantity,
        orderedQuantity: remainingQuantity,
      };
    }),
    discount,
    discountType: {
      label: startCase(discountType),
      value: discountType,
    },
    subtotal,
    total,
    itemType: {
      label: itemType,
      value: itemType,
    },
  };

  if (
    purchaseOrder?.supplier &&
    JSON.stringify(purchaseOrder?.supplier) !== "{}"
  ) {
    data.supplier = {
      ...purchaseOrder?.supplier,
      label: purchaseOrder?.supplier?.name,
      value: purchaseOrder?.supplier?.id,
    };
  }
  return data;
};

function GRNForm({}) {
  const ref = useRef();
  const [state, setState] = useState({});
  const [initialState, setInitialState] = useState(null);
  const [searchParams] = useSearchParams();
  const [purchaseOrderTableData, setPurchaseOrderData] = useState([]);
  const { redirect } = useTaskRedirect();
  const { isOpen, closeModal, openModal } = useModal(false);

  const purchaseOrderId = searchParams.get("purchaseOrder");
  const isCreatingGRNFromPurchaseOrder = Boolean(purchaseOrderId);

  const { data: purchaseOrderData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
      state.purchaseOrder?.value || purchaseOrderId,
    ],
    () =>
      getDynamicObjectRecordById(
        dynamicObjectMap.get("PurchaseOrderObjectName"),
        state.purchaseOrder?.value || purchaseOrderId
      ),
    {
      enabled: Boolean(state.purchaseOrder?.value) || Boolean(purchaseOrderId),
    }
  );

  const { data: supplierPurchaseOrderData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
      state?.supplier?.value,
    ],
    () =>
      getDynamicObjectRecords(
        kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
        {
          supplier: state?.supplier?.value,
          "status[in]": "Approved,Partial",
          queryMode: "Deep",
        }
      ),
    {
      enabled: Boolean(state?.supplier?.value),
    }
  );

  useEffect(() => {
    if (
      supplierPurchaseOrderData?.data &&
      supplierPurchaseOrderData?.data?.length &&
      state?.supplier
    ) {
      const formattedPurchaseOrder = supplierPurchaseOrderData?.data?.map((r) =>
        formatPurchaseOrder(r)
      );
      setPurchaseOrderData(formattedPurchaseOrder);
      openModal();
    } else {
      setPurchaseOrderData([]);
    }
  }, [supplierPurchaseOrderData, state?.supplier]);

  useEffect(() => {
    if (purchaseOrderData) {
      const formattedPurchaseOrder = formatPurchaseOrder(purchaseOrderData);
      setInitialState(formattedPurchaseOrder);
    }
  }, [purchaseOrderData]);

  const prepareItemDetail = (itemDetail) => {
    const { acceptedQuantity, rejectedQuantity, shortQuantity } = itemDetail;

    return {
      ...itemDetail,
      suppliedQuantity:
        parseInt(Number(acceptedQuantity ?? 0), 10) +
        parseInt(Number(rejectedQuantity ?? 0), 10),
      orderedQuantity:
        parseInt(Number(acceptedQuantity ?? 0), 10) +
        parseInt(Number(rejectedQuantity ?? 0), 10) +
        parseInt(Number(shortQuantity ?? 0), 10),
    };
  };

  const setItemDetail = (key, value) => {
    let calculatedItemDetail = value;
    if (value && value.length > 0) {
      calculatedItemDetail = calculatedItemDetail.map((item) =>
        prepareItemDetail(item)
      );
    } else if (value) {
      calculatedItemDetail = prepareItemDetail(value);
    }

    ref.current.setFormValue(key, calculatedItemDetail);
  };

  const onStateChange = (key, value) => {
    if (key === "supplier") {
      setState({
        ...state,
        [key]: value,
      });
    }

    if (key === "purchaseOrder") {
      setState((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    }

    if (key === "itemDetail") {
      setItemDetail(key, value);
    }

    if (key === "itemDetailTable") {
      setItemDetail(key, value);
    }
  };

  const handleConfirm = () => {
    if (purchaseOrderTableData.length) {
      const selectedPurchaseOrder = purchaseOrderTableData.find(
        (q) => q.isSelected
      );

      if (selectedPurchaseOrder) {
        ref.current.setFormState(selectedPurchaseOrder);
      }
    }

    closeModal();
  };

  return (
    <BoxedContent>
      <AlertModal
        icon="file-check-stroke-icon"
        iconClass="success"
        title="Select Purchase Order"
        subtitle="Selected supplier has following purchase orders"
        onClose={closeModal}
        isOpen={isOpen}
        onConfirm={handleConfirm}
        size="large"
      >
        {purchaseOrderTableData.length ? (
          <TableWithCheckbox
            data={purchaseOrderTableData}
            columns={getPurchaseOrderTableColumns()}
            setData={setPurchaseOrderData}
          />
        ) : null}
      </AlertModal>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        objectName={dynamicObjectMap.get("GoodsReceiptNoteObjectName")}
        showHeader
        showLinkedViews
        onStateChange={onStateChange}
        onSuccess={(id) =>
          redirect(-1, {
            recordId: id,
            success: true,
          })}
        navigate={false}
        readOnlyFields={
          isCreatingGRNFromPurchaseOrder ? ["supplier", "purchaseOrder"] : []
        }
      />
    </BoxedContent>
  );
}

export default GRNForm;
